import React from 'react';
import { useLocation } from 'react-router-dom';

import ApplicationList from '../../ApplicationManagement/ApplicationList/ApplicationList';
import ApplicationDetailsWithOperations from '../../ApplicationManagement/ViewApplication/ApplicationDetailsWithOperations';
import OperationDetails from '../../OperationManagement/ViewOperation/OperationDetails';
import AddApplicationPage from '../../ApplicationManagement/AddApplication/AddApplicationPage';
import AddOperationPage from '../../OperationManagement/AddOperation/AddOperationPage';
import EditApplicationPage from '../../ApplicationManagement/EditApplication/EditApplicationPage';
import EditOperationPage from '../../OperationManagement/EditOperation/EditOperationPage';
import ExportOperationToTaskPage from '../../OperationManagement/ExportOperationToTask/ExportOperationToTaskPage';

import { ROUTES } from '../types/navigationTypes';
import { useNavigation } from '../hooks/useNavigation';
import { useStore } from 'src/stores/store';
import { parsePathToRoute } from '../utils/navigationUtils';

const ViewRenderer: React.FC = () => {
	const { navigate } = useNavigation();
	const { currentView, selectedApplication, selectedOperation } = useStore();
	const location = useLocation();

	const routeInfo = parsePathToRoute(location.pathname + location.hash);
	// Fall back to URL-derived applicationId when store is empty (e.g., after page refresh)
	const applicationId =
		selectedApplication?.applicationId || routeInfo.applicationId || '';

	const applicationListRefreshRef = React.useRef<{
		fetchData: () => void;
	} | null>(null);
	const operationListRefreshRef = React.useRef<{
		fetchData: () => void;
	} | null>(null);

	const renderApplicationsList = () => (
		<ApplicationList ref={applicationListRefreshRef} />
	);

	const renderApplicationDetails = () => (
		<ApplicationDetailsWithOperations
			applicationId={applicationId}
			operationListRef={operationListRefreshRef}
		/>
	);

	const renderOperationDetails = () => (
		<OperationDetails
			operationId={selectedOperation?.operationId || ''}
			supportedOperation={selectedOperation || undefined}
		/>
	);

	const renderAddApplication = () => (
		<AddApplicationPage
			onCancel={() => navigate(ROUTES.APPLICATIONS, '/applicationManagement')}
		/>
	);

	const renderAddOperation = () => (
		<AddOperationPage
			onCancel={() =>
				navigate(
					ROUTES.APPLICATION_DETAILS,
					`/applicationManagement/${applicationId}`
				)
			}
			applicationId={applicationId}
			applicationName={selectedApplication?.applicationName || ''}
		/>
	);

	const renderEditApplication = () => (
		<EditApplicationPage
			onCancel={() =>
				navigate(
					ROUTES.APPLICATION_DETAILS,
					`/applicationManagement/${applicationId}`
				)
			}
			applicationId={applicationId}
			applicationName={selectedApplication?.applicationName || ''}
		/>
	);

	const renderEditOperation = () => (
		<EditOperationPage
			onCancel={() =>
				navigate(
					ROUTES.OPERATION_DETAILS,
					`/applicationManagement/${applicationId}/${
						selectedOperation?.operationId || ''
					}`
				)
			}
			operationId={selectedOperation?.operationId || ''}
			operationName={selectedOperation?.operationName || ''}
			applicationId={applicationId}
		/>
	);

	const renderExportOperationToTask = () => (
		<ExportOperationToTaskPage
			onCancel={() => {
				if (applicationId) {
					navigate(
						ROUTES.APPLICATION_DETAILS,
						`/applicationManagement/${applicationId}`
					);
				}
			}}
			operationId={selectedOperation?.operationId || ''}
			operation={selectedOperation || undefined}
		/>
	);

	const viewRenderers = {
		[ROUTES.APPLICATIONS]: renderApplicationsList,
		[ROUTES.APPLICATION_DETAILS]: renderApplicationDetails,
		[ROUTES.OPERATION_DETAILS]: renderOperationDetails,
		[ROUTES.ADD_APPLICATION]: renderAddApplication,
		[ROUTES.ADD_OPERATION]: renderAddOperation,
		[ROUTES.EDIT_APPLICATION]: renderEditApplication,
		[ROUTES.EDIT_OPERATION]: renderEditOperation,
		[ROUTES.EXPORT_OPERATION_TO_TASK]: renderExportOperationToTask,
	};

	const renderer = viewRenderers[currentView];

	return renderer ? renderer() : null;
};

export default ViewRenderer;
